/**
 * ScheduledBatchList — E-93 Story 93.6.
 *
 * Renders a table of ütemezett bulk-email batch-eket with a per-row
 * cancel button. Auto-polls every 30s so the franchise manager sees
 * the QUEUED → DISPATCHING → COMPLETED transitions live without a
 * manual refresh.
 *
 * Adversarial picks:
 *
 * - **No optimistic delete.** Cancel waits for server confirmation +
 *   refetch. A race-loser (dispatcher claimed the row in the same
 *   millisecond) gets a 409 → toast surfaces "already DISPATCHING"
 *   instead of silently lying to the user.
 * - **Cancel button only enabled for PENDING/QUEUED.** DISPATCHING
 *   and COMPLETED rows show no button at all (greying it out would
 *   tempt operators to assume "I can cancel during dispatch if I'm
 *   fast enough" — which is not true; see 93.5/93.6 service header).
 * - **Localised relative scheduledAt.** Uses `Intl.RelativeTimeFormat`
 *   for "in 2 hours" / "in 3 days" — gives operator immediate sense
 *   of urgency vs. parsing an ISO timestamp.
 */

import type { ScheduledBatchRow } from '@/api/bulk-email';
import {
  CancelScheduledModal,
  type CancelScheduledModalProps,
} from '@/components/messaging/CancelScheduledModal';
import { useScheduledBatches } from '@/hooks/use-scheduled-batches';
import { useTranslation } from '@kgc/i18n';
import { Button, toast } from '@kgc/ui';
import type { JSX } from 'react';
import { useState } from 'react';

const POLL_MS = 30_000;

export function ScheduledBatchList(): JSX.Element {
  const { t } = useTranslation();
  const { data, totalCount, isLoading, error, cancel, isCancellingId } = useScheduledBatches({
    pollMs: POLL_MS,
  });

  const [pendingCancel, setPendingCancel] = useState<ScheduledBatchRow | null>(null);

  async function handleConfirmCancel(): Promise<void> {
    const target = pendingCancel;
    if (!target) return;
    setPendingCancel(null);
    try {
      const result = await cancel(target.id);
      if (result.outcome === 'cancelled') {
        toast.success(t('email.scheduled.cancelToast.success'));
      } else if (result.outcome === 'already_cancelled') {
        toast.info(t('email.scheduled.cancelToast.alreadyCancelled'));
      } else if (result.outcome === 'not_cancellable') {
        toast.warning(t('email.scheduled.cancelToast.notCancellable', { status: result.status }));
      } else {
        toast.error(t('email.scheduled.cancelToast.notFound'));
      }
    } catch (err) {
      // 409 from backend surfaces as ApiError thrown by api.post — surface
      // a generic warning. The race-fresh refetch below brings the table
      // back to the canonical state regardless.
      const message = err instanceof Error ? err.message : String(err);
      if (/409/.test(message)) {
        toast.warning(t('email.scheduled.cancelToast.race'));
      } else {
        toast.error(t('email.scheduled.cancelToast.error'));
      }
    }
  }

  return (
    <section aria-label={t('email.scheduled.sectionAriaLabel')} className="space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {t('email.scheduled.title')}{' '}
          <span className="text-sm text-muted-foreground">({totalCount})</span>
        </h2>
      </header>

      {error ? (
        <p className="text-sm text-destructive">{t('email.scheduled.error', { message: error })}</p>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">{t('email.scheduled.loading')}</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('email.scheduled.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-2 pr-3 font-medium">{t('email.scheduled.col.template')}</th>
                <th className="py-2 pr-3 font-medium">{t('email.scheduled.col.scheduledAt')}</th>
                <th className="py-2 pr-3 font-medium">{t('email.scheduled.col.recipients')}</th>
                <th className="py-2 pr-3 font-medium">{t('email.scheduled.col.status')}</th>
                <th className="py-2 pr-3 font-medium">{t('email.scheduled.col.legalBasis')}</th>
                <th className="py-2" aria-label={t('email.scheduled.col.actions')} />
              </tr>
            </thead>
            <tbody>
              {data.map(row => (
                <tr key={row.id} className="border-t">
                  <td className="py-2 pr-3">{row.templateSlug}</td>
                  <td className="py-2 pr-3">
                    <span title={row.scheduledAt ?? undefined}>
                      {formatScheduledAt(row.scheduledAt)}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    {row.enqueuedCount}
                    <span className="text-muted-foreground"> / {row.totalRecipients}</span>
                    {row.skippedCount > 0 ? (
                      <span className="text-xs text-muted-foreground">
                        {' '}
                        ({t('email.scheduled.skippedSuffix', { count: row.skippedCount })})
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="rounded bg-muted px-2 py-0.5 text-xs">{row.status}</span>
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{row.legalBasis}</td>
                  <td className="py-2 text-right">
                    {isCancellable(row.status) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isCancellingId === row.id}
                        onClick={() => setPendingCancel(row)}
                      >
                        {isCancellingId === row.id
                          ? t('email.scheduled.cancelling')
                          : t('email.scheduled.cancelButton')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CancelScheduledModal
        open={pendingCancel !== null}
        onOpenChange={(next: boolean) => {
          if (!next) setPendingCancel(null);
        }}
        recipientCount={pendingCancel?.enqueuedCount ?? 0}
        templateSlug={pendingCancel?.templateSlug ?? ''}
        onConfirm={handleConfirmCancel}
      />
    </section>
  );
}

function isCancellable(status: ScheduledBatchRow['status']): boolean {
  return status === 'PENDING' || status === 'QUEUED';
}

/**
 * Format the scheduled timestamp as a human-friendly relative string
 * ("in 2 hours", "in 3 days") falling back to the raw ISO when the
 * row carries no scheduledAt (immediate batch stuck in PENDING).
 */
function formatScheduledAt(iso: string | null): string {
  if (!iso) return '—';
  const target = new Date(iso);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();

  // Past: render absolute local time, not "5 minutes ago" (which would
  // be confusing for an "ütemezett" list).
  if (diffMs < 0) {
    return target.toLocaleString();
  }

  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(minutes, 'minute');
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(hours, 'hour');
  }
  const days = Math.round(hours / 24);
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(days, 'day');
}

// Re-export the modal props shape for storybook / tests if needed.
export type { CancelScheduledModalProps };
